/**
 * Labstep
 *
 * @module containers/Entity/Read/Entities
 * @desc Container for reading entities
 */

import type { APICallOptions } from 'labstep-web/models';
import type { EntityName } from 'labstep-web/services/schema/schema';
import { readEntities } from 'labstep-web/state/actions/entity';
import {
  selectEntities,
  selectReadEntitiesStatus,
  selectSingleItems,
} from 'labstep-web/state/selectors/entity';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type {
  IEntityReadEntitiesContainerChildrenProps,
  IEntityReadEntitiesContainerProps,
} from './types';

export const EntityReadEntitiesContainer = <
  T extends EntityName = EntityName,
>({
  children,
  entityName,
  params,
}: IEntityReadEntitiesContainerProps<T>): React.ReactElement | null => {
  const dispatch = useDispatch();

  // Use selectors to get state
  const status = useSelector((state) =>
    selectReadEntitiesStatus(state, entityName, params),
  );

  const getSingle = params && params.get_single === 1;
  const entity = useSelector((state) =>
    getSingle
      ? selectSingleItems(state, entityName, params)
      : undefined,
  );
  const entities = useSelector((state) =>
    !getSingle
      ? selectEntities(state, entityName, params)
      : undefined,
  );

  // Memoized read function
  const read = useCallback(
    (options?: APICallOptions) => {
      dispatch(readEntities(entityName, params, options));
    },
    [dispatch, entityName, params],
  );

  return children({
    entity,
    entities,
    status,
    read,
  });
};
